/**
 * BottomNav — Mobile bottom navigation bar (< 768px only)
 *
 * Renders 4 primary nav tabs + a "More" button that opens the full drawer.
 * Position: fixed to the bottom of the viewport, safe-area aware.
 * Hidden on tablet and desktop via the parent layout (not rendered there).
 *
 * Tab selection:
 *   Dashboard  →  /
 *   Orders     →  /work-orders
 *   Customers  →  /customers
 *   Sales      →  /invoices
 *   More       →  opens MobileDrawer (calls onMoreClick)
 */

import React from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, ClipboardList, Users, Receipt, Menu } from "lucide-react";

const TABS = [
  { to: "/",             label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/work-orders",  label: "Orders",    icon: ClipboardList },
  { to: "/customers",    label: "Customers", icon: Users },
  { to: "/invoices",     label: "Sales",     icon: Receipt },
];

export default function BottomNav({ onMoreClick }) {
  return (
    <nav
      data-testid="bottom-nav"
      style={{
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        height: "var(--flg-bottom-nav-h)",
        paddingBottom: "var(--flg-safe-bottom)",
        background: "var(--flg-glass-bg-strong)",
        backdropFilter: "var(--flg-glass-blur)",
        WebkitBackdropFilter: "var(--flg-glass-blur)",
        borderTop: "1px solid var(--flg-border)",
        display: "flex",
        zIndex: "var(--flg-z-sticky)",
      }}
    >
      {TABS.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-testid={`bottom-nav-${label.toLowerCase()}`}
          style={({ isActive }) => ({
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            fontSize: 10,
            fontWeight: isActive ? 600 : 400,
            color: isActive ? "var(--flg-primary)" : "var(--flg-fg-muted)",
            textDecoration: "none",
            padding: "6px 4px",
            transition: "color 120ms",
            minWidth: 0,
          })}
        >
          {({ isActive }) => (
            <>
              <Icon
                size={22}
                strokeWidth={isActive ? 2.2 : 1.75}
                style={{ flexShrink: 0 }}
              />
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", textAlign: "center" }}>
                {label}
              </span>
            </>
          )}
        </NavLink>
      ))}

      {/* More — opens full navigation drawer */}
      <button
        onClick={onMoreClick}
        data-testid="bottom-nav-more"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          fontSize: 10,
          fontWeight: 400,
          color: "var(--flg-fg-muted)",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "6px 4px",
          minWidth: 0,
        }}
        aria-label="Open full navigation menu"
      >
        <Menu size={22} strokeWidth={1.75} style={{ flexShrink: 0 }} />
        <span>More</span>
      </button>
    </nav>
  );
}
